import { createContext, useContext, useState, useEffect, ReactNode } from "react";

export interface SessionState {
  visitedDemos: string[];
  addVisit: (demoId: string) => void;
  clearVisits: () => void;
}

const SessionContext = createContext<SessionState | null>(null);

const STORAGE_KEY = "sloe_lab_visited_demos";
const MAX_HISTORY = 10;

export function SessionProvider({ children }: { children: ReactNode }) {
  // Hydrate from sessionStorage on mount
  const [visitedDemos, setVisitedDemos] = useState<string[]>(() => {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) return parsed.slice(-MAX_HISTORY);
      }
    } catch {
      // Ignore parse errors and start fresh
    }
    return [];
  });

  // Persist to sessionStorage whenever visitedDemos changes
  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(visitedDemos));
    } catch {
      // Storage quota exceeded or disabled — non-fatal
    }
  }, [visitedDemos]);

  const addVisit = (demoId: string) => {
    if (!demoId) return;
    setVisitedDemos((prev) => {
      // Remove any existing occurrence of this demoId, then append at end.
      // This gives us an ordered-by-most-recent-visit list without duplicates.
      const without = prev.filter((id) => id !== demoId);
      return [...without, demoId].slice(-MAX_HISTORY);
    });
  };

  const clearVisits = () => {
    setVisitedDemos([]);
    try {
      sessionStorage.removeItem(STORAGE_KEY);
    } catch {
      // Non-fatal
    }
  };

  return (
    <SessionContext.Provider value={{ visitedDemos, addVisit, clearVisits }}>
      {children}
    </SessionContext.Provider>
  );
}

export function useSession(): SessionState {
  const ctx = useContext(SessionContext);
  if (!ctx) {
    return {
      visitedDemos: [],
      addVisit: () => {},
      clearVisits: () => {},
    };
  }
  return ctx;
}
